import type {PendingLead, EventLead} from "~/types/Events";
import {SyncStatus} from "~/types/Events";

export const useOfflineLeadQueueStore = defineStore('OfflineLeadQueueStore', () => {
  const {$api} = useNuxtApp();
  const { isOnline } = useConnectivity();

  const MAX_ATTEMPTS = 999; // permanent failure sentinel
  const SYNCED_LINGER_MS = 3000; // keep the "Synced" confirmation visible briefly before removing
  const REJECTED_LINGER_MS = 5000; // rejections show a reason — give a bit longer to read it

  const queue: Ref<PendingLead[]> = ref([]);
  const isSyncing = ref(false);

  // Removal timers for items in a transient outcome state (SYNCED / REJECTED). Not persisted:
  // these states are dropped during hydration instead (the outcome is already final server-side).
  const finalizeTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const clearFinalizeTimer = (localId: string) => {
    const timer = finalizeTimers.get(localId);
    if(timer) {
      clearTimeout(timer);
      finalizeTimers.delete(localId);
    }
  };

  // Let an item linger in its outcome state as a confirmation, then run an optional side effect
  // and drop the card. Used for both SYNCED (add the lead to the list) and REJECTED (no-op).
  const scheduleRemoval = (localId: string, delayMs: number, beforeRemove?: () => void) => {
    clearFinalizeTimer(localId);
    const timer = setTimeout(() => {
      finalizeTimers.delete(localId);
      beforeRemove?.();
      queue.value = queue.value.filter(l => l.localId !== localId);
    }, delayMs);
    finalizeTimers.set(localId, timer);
  };

  const pendingCount = computed(() =>
    queue.value.filter(l => l.syncStatus === SyncStatus.PENDING || l.syncStatus === SyncStatus.SYNCING).length
  );

  const hasPending = computed(() =>
    queue.value.some(l =>
      l.syncStatus === SyncStatus.PENDING
      // A persisted SYNCING item is a leftover from an interrupted run — still needs syncing.
      || l.syncStatus === SyncStatus.SYNCING
      || (l.syncStatus === SyncStatus.FAILED && l.attempts < MAX_ATTEMPTS)
    )
  );

  const pendingForEvent = (eventId: number) =>
    computed(() => queue.value.filter(l => l.eventId === eventId));

  const enqueue = (eventId: number, code: string): PendingLead | null => {
    const existing = queue.value.find(
      l => l.code === code && l.eventId === eventId
    );
    if(existing) {
      return null;
    }

    const pending: PendingLead = {
      localId: typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      eventId,
      code,
      scannedAt: new Date().toISOString(),
      syncStatus: SyncStatus.PENDING,
      attempts: 0,
      lastError: null,
    };

    queue.value.push(pending);
    return pending;
  };

  const syncAll = async () => {
    if(isSyncing.value) {
      return;
    }
    isSyncing.value = true;

    // Past the guard no run is active, so any item still marked SYNCING is a leftover from a
    // previous run that was interrupted (navigation, reload, dropped connection). Reset it to
    // PENDING so it's picked up below instead of being stranded as "Syncing…" forever.
    queue.value.forEach(l => {
      if(l.syncStatus === SyncStatus.SYNCING) {
        l.syncStatus = SyncStatus.PENDING;
      }
    });

    try {
      const toSync = queue.value.filter(
        l => (l.syncStatus === SyncStatus.PENDING || l.syncStatus === SyncStatus.FAILED) && l.attempts < MAX_ATTEMPTS
      );

      for(const item of toSync) {
        // The app's connectivity state, not navigator.onLine: a stuck flag stopped the queue
        // from ever draining even though every request would have succeeded.
        if(!isOnline.value) break;

        item.syncStatus = SyncStatus.SYNCING;
        item.attempts++;

        try {
          const lead: EventLead = await $api.v2.clientScanEventLead(item.eventId, item.code);
          // Linger as a "Synced" confirmation, then add the lead to the list + drop the card.
          item.syncStatus = SyncStatus.SYNCED;
          item.lastError = null;
          const contactsStore = useContactsStore();
          scheduleRemoval(item.localId, SYNCED_LINGER_MS, () => contactsStore.upsertLead(lead));
        } catch (e: any) {
          const status = e?.status || e?.statusCode || e?.response?.status;
          if(status === 422 || status === 404) {
            // Permanent rejection (invalid/expired code, or already scanned). Show why, briefly,
            // then drop it — retrying won't help.
            item.syncStatus = SyncStatus.REJECTED;
            item.lastError = e?.data?.message
              || (status === 404 ? 'Lead not found — the code may be invalid or expired.' : 'This lead could not be synced.');
            scheduleRemoval(item.localId, REJECTED_LINGER_MS);
          } else if(!status) {
            // No HTTP status = transport failure (connection dropped mid-request, timeout). Not
            // the lead's fault, so don't surface a scary "failed" card or burn an attempt — keep
            // it PENDING and let the loop's offline check / next sync trigger retry it cleanly.
            // This keeps a large offline batch tidy when draining over flaky event WiFi.
            item.syncStatus = SyncStatus.PENDING;
            item.attempts--;
          } else {
            // Real server error (5xx) — retryable; surface it with a Retry action.
            item.syncStatus = SyncStatus.FAILED;
            item.lastError = e?.data?.message || e?.message || 'Sync failed';
          }
        }
      }
    } finally {
      // Never leave the queue mid-flight: anything still SYNCING here (e.g. loop broke on a
      // dropped connection before the request settled) goes back to PENDING for the next run.
      queue.value.forEach(l => {
        if(l.syncStatus === SyncStatus.SYNCING) {
          l.syncStatus = SyncStatus.PENDING;
        }
      });
      isSyncing.value = false;
    }
  };

  const dismiss = (localId: string) => {
    clearFinalizeTimer(localId);
    queue.value = queue.value.filter(l => l.localId !== localId);
  };

  const clearAll = () => {
    finalizeTimers.forEach(clearTimeout);
    finalizeTimers.clear();
    queue.value = [];
  };

  return {
    queue,
    isSyncing,
    pendingCount,
    hasPending,
    pendingForEvent,
    enqueue,
    syncAll,
    dismiss,
    clearAll,
  };
}, {
  persist: {
    pick: ['queue'],
    // The queue grows unbounded as leads are scanned offline. The Nuxt module defaults every
    // store to cookie storage, but a cookie silently stops accepting writes past the browser's
    // ~4KB limit (~15-18 leads) — dropping scans — and is attached to every same-domain request.
    // This is client-only data that never belongs in a request header, so persist it to
    // localStorage (~5MB, client-side only) instead.
    storage: piniaPluginPersistedstate.localStorage(),
    // Normalise transient states that can't survive a reload:
    //  - SYNCED / REJECTED are final outcomes whose linger timer is gone after reload, so drop
    //    them (a synced lead reloads from the API; a rejected one is permanently gone).
    //  - SYNCING means a run was interrupted mid-flight; demote to PENDING so it's retried
    //    instead of loading as a frozen "Syncing…" that no trigger ever picks up.
    afterHydrate: (ctx) => {
      const store = ctx.store as unknown as { queue: PendingLead[] };
      store.queue = (store.queue ?? [])
        .filter(l => l.syncStatus !== SyncStatus.SYNCED && l.syncStatus !== SyncStatus.REJECTED)
        .map(l => l.syncStatus === SyncStatus.SYNCING ? { ...l, syncStatus: SyncStatus.PENDING } : l);
    },
  },
});
